import type { Control, FieldArrayPath, FieldPath, FieldValues } from 'react-hook-form';
import { useFieldArray, useWatch } from 'react-hook-form';

/**
 * Similar as useFieldArray, but returns up to date fields (not just default values).
 * Note that simple getting of actual data from useWatch doesn't work well when removing items from collection in form model
 * so we have to get the actual array from useFieldArray and update its values to latest ones.
 * @param param0
 * @returns
 */
export const useControlledFieldArray = <
  TFormModel extends FieldValues,
  TFieldArrayName extends FieldArrayPath<TFormModel> & FieldPath<TFormModel> =
    FieldArrayPath<TFormModel> & FieldPath<TFormModel>,
>({
  control,
  name,
}: {
  control: Control<TFormModel>;
  name: TFieldArrayName;
}) => {
  const {
    append,
    remove,
    fields: arrayFieldsDefaultValues,
  } = useFieldArray({ control, name, keyName: '_rhf_key' });
  const formArrayCurrent = useWatch({ control, name });

  // Inspired by handling of controlled field array as described here: https://react-hook-form.com/docs/usefieldarray
  const controlledFields: typeof arrayFieldsDefaultValues = arrayFieldsDefaultValues.map(
    (arrayFieldDefaultValue, index) => {
      return {
        ...arrayFieldDefaultValue,
        ...formArrayCurrent[index],
      };
    },
  );

  return { append, remove, fields: controlledFields };
};
